'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { RecentlyViewedCarousel } from '@/components/brand-kit/recently-viewed-carousel'
import { clearRecentProducts, recordRecentProduct, useRecentProducts, type RecentProduct } from '@/lib/recently-viewed-store'
import { compareHref, useSavedCompare } from './saved-compare-store'
import type { HomeProduct } from '@/lib/homepage-2026/types'
import { correctProductLead } from '@/lib/product-lead-images'
import { formatPrice } from './format'

export function RecentViewRecorder({ product }: { product: Omit<RecentProduct, 'viewedAt'> }) {
  const { id, fin, brand, title, href, image } = product
  useEffect(() => { recordRecentProduct({ id, fin, brand, title, href, image }) }, [id, fin, brand, title, href, image])
  return null
}

/** Shared branded carousel, with device-local history and only page-provided live prices. */
export function RecentReturn({ currentProducts = [], excludeId, showCompare = false, title = 'Recently viewed', limit = 12, clearable = false }: {
  currentProducts?: HomeProduct[]; excludeId?: string; showCompare?: boolean; title?: string; limit?: number; clearable?: boolean
}) {
  const recent = useRecentProducts().filter(p => p.id !== excludeId).slice(0, Math.max(0, limit))
  const { compare, clearCompare } = useSavedCompare()
  const live = new Map(currentProducts.map(p => [p.id, p]))
  if (!recent.length && !(showCompare && compare.length)) return null
  return (
    <section aria-label="Continue shopping" className="hp26" data-recent-return style={{ background: '#fff' }}>
      {showCompare && compare.length > 0 && <div className="wrap" style={{ paddingTop: 24, paddingBottom: 24 }}>
      {/* FRIDGE-1109 (owner: "Looks weird"): two bare text links above the footer. Now the house
          card — count, an orange primary pill to continue, a quiet blue secondary to clear
          (docs/Design.md §22.5, §22.10). Renders only while something is being compared. */}
      {showCompare && compare.length > 0 && <div data-compare-return className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4" style={{ marginBottom: 20, boxShadow: '0 8px 28px rgba(16, 24, 40, .08)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 9999, background: '#f0f6ff', color: '#0454c0', fontWeight: 800, fontSize: 15, flex: 'none' }} aria-hidden="true">{compare.length}</span>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#101828', flex: '1 1 160px', minWidth: 0 }}>You’re comparing {compare.length} {compare.length === 1 ? 'model' : 'models'}</span>
        <Link href={compareHref(compare)} className="inline-flex items-center justify-center rounded-full font-bold" style={{ minHeight: 44, padding: '0 22px', background: '#ff9951', color: '#101828', fontSize: 14, textDecoration: 'none', flex: 'none' }}>Continue comparing →</Link>
        <button type="button" onClick={clearCompare} className="inline-flex items-center justify-center rounded-full font-semibold" style={{ minHeight: 44, padding: '0 16px', background: '#f0f6ff', color: '#0454c0', fontSize: 13, border: '1px solid #cfe1fb', cursor: 'pointer', flex: 'none' }}>Clear</button>
      </div>}
      </div>}
      {recent.length > 0 && <RecentlyViewedCarousel
        eyebrow={title}
        bgClass="bg-white"
        pool={recent.map(p => {
          const current = live.get(p.id)
          return { id: p.id, product: {
            brand: current?.brand ?? p.brand,
            title: current?.title ?? p.title,
            image: correctProductLead([current?.image ?? p.image])[0],
            href: current?.href ?? p.href,
            identifier: current?.fin ?? p.fin,
            price: current ? formatPrice(current.priceCents) : '',
            badges: current?.badges ?? [],
            rating: current?.rating?.rating,
            reviews: current?.rating?.reviews,
            images: current?.alternateImage ? correctProductLead([current.image, current.alternateImage]) : undefined,
          } }
        })}
      />}
      {clearable && recent.length > 0 && <div className="wrap"><button type="button" className="hdrpanel-clr" onClick={clearRecentProducts}>Clear all</button></div>}

    </section>
  )
}
